import React, { lazy, useState } from 'react';

import { useTranslation } from '~/app';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '~/components/Dialog/Dialog';

/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

type AuthMode = 'login' | 'register';

interface AuthDialogProps {
  modalOpen: boolean;
  initialMode?: AuthMode;
  prefilledEmail?: string;
  prefilledPassword?: string;
  prefilledFirebaseIdToken?: string;
  onLoginSuccess: () => void;
  onRegistrationSuccess: () => void;
  onCancelled: () => void;
  onForgotPassword: () => void;
}

const LoginFirebaseLazy = lazy(() => import('./LoginFirebase'));
const RegisterFirebaseLazy = lazy(() => import('./RegisterFirebase'));

/**
 * Initial form state comes from props; parent must pass a key so the dialog remounts
 * when opening (e.g. key={`${authState}-${prefilledEmail}-...`}) to avoid syncing props in effects.
 */
const AuthDialog = ({
  modalOpen,
  initialMode = 'login',
  prefilledEmail = '',
  prefilledPassword = '',
  prefilledFirebaseIdToken,
  onLoginSuccess,
  onRegistrationSuccess,
  onCancelled,
  onForgotPassword,
}: AuthDialogProps): JSX.Element => {
  const { t } = useTranslation();
  const [activeMode, setActiveMode] = useState<AuthMode>(initialMode);
  const [email, setEmail] = useState(prefilledEmail);
  const [password, setPassword] = useState(prefilledPassword);
  const [firebaseIdToken, setFirebaseIdToken] = useState<string | undefined>(
    prefilledFirebaseIdToken,
  );

  const handleCreateAccount = (email: string, password: string, firebaseIdToken?: string) => {
    setEmail(email);
    setPassword(password);
    setFirebaseIdToken(firebaseIdToken);
    setActiveMode('register');
  };

  const handleBackToLogin = () => {
    setActiveMode('login');
  };

  const isLoginMode = activeMode === 'login';

  return (
    <Dialog open={modalOpen} onOpenChange={(open) => !open && onCancelled()}>
      <DialogContent
        size={isLoginMode ? 'lg' : '2xl'}
        className={isLoginMode ? 'sm:max-w-xl' : 'max-w-5xl'}
      >
        <DialogTitle>
          {isLoginMode ? t('start_accountLoginDialogTitle') : t('start_accountRegisterDialogTitle')}
        </DialogTitle>
        <DialogDescription>
          {isLoginMode
            ? t('start_accountLoginDialogDescription')
            : t('start_accountRegisterDialogContent')}
        </DialogDescription>

        {/* Tab Navigation */}
        <div className="-mx-6 mb-4 flex border-b border-gray-200 px-6">
          <button
            type="button"
            onClick={() => setActiveMode('login')}
            className={`-mb-px flex-1 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
              isLoginMode
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            } `}
          >
            {t('start_accountLoginButtonText')}
          </button>
          <button
            type="button"
            onClick={() => setActiveMode('register')}
            className={`-mb-px flex-1 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
              !isLoginMode
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            } `}
          >
            {t('start_accountRegisterButtonText')}
          </button>
        </div>

        <div className="@container mt-4">
          {typeof window !== 'undefined' && (
            <React.Suspense fallback={<div />}>
              {isLoginMode ? (
                <LoginFirebaseLazy
                  onLoginSuccess={onLoginSuccess}
                  onForgotPassword={onForgotPassword}
                  onCreateAccount={handleCreateAccount}
                />
              ) : (
                <RegisterFirebaseLazy
                  email={email}
                  password={password}
                  firebaseIdToken={firebaseIdToken}
                  onFirebaseRegistrationSuccess={onRegistrationSuccess}
                  onBackToLogin={handleBackToLogin}
                />
              )}
            </React.Suspense>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthDialog;
